import React, { useEffect, useMemo, useRef, useState } from 'react';
import PageLayout from '../../../../ui/PageLayout.jsx';
import PageHeader from '../../../../ui/PageHeader.jsx';
import Button from '../../../../ui/Button.jsx';
import GeneratePlanSheet from './GeneratePlanSheet.jsx';
import WidgetShell from '../../../../ui/WidgetShell.jsx';
import { SmallText } from '../../../../ui/Typography.jsx';
import { usePlannerSuggestions } from '../../hooks/usePlannerSuggestions.js';
import { useNotifications } from '../../../../contexts/NotificationsContext.jsx';
import { buildPlannerAlerts } from '../../logic/plannerAlerts.js';
import { evaluateCapacity, capacityLevelLabel } from '../../logic/capacityEngine.js';
import { buildCapacitySignals } from '../../logic/capacitySignals.js';
import { SuggestionType } from '../../logic/suggestionTypes.js';
import { PlannerProvider, usePlanner } from '../../contexts/PlannerContext';
import ListView from './views/ListView';
import KanbanView from './views/KanbanView';
import CalendarView from './views/CalendarView';
import GanttView from './views/GanttView';

function PlannerShellContent({ children, products = [], firings = [], tasks = [], capacity = null }) {
  const [view, setView] = useState('list');
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const { notifyAndInbox, addNotification } = useNotifications() || {};
  const computedCapacity = useMemo(
    () => evaluateCapacity({ tasks, firings }),
    [tasks, firings]
  );
  const { suggestions } = usePlannerSuggestions({ tasks, capacity: computedCapacity });
  const { addGeneratedTasks, updateTask } = usePlanner();

  const alerts = useMemo(() => {
    const base = buildPlannerAlerts({ tasks, capacity: computedCapacity });
    const signals = buildCapacitySignals({ tasks, capacity: computedCapacity });
    return [...base, ...signals];
  }, [tasks, computedCapacity]);
  const addedAlertIds = useRef(new Set());

  const applySuggestion = async (suggestion) => {
    if (!suggestion) return;
    const task = tasks.find((t) => t.id === suggestion.taskId);
    if (suggestion.type === SuggestionType.MOVE_TASK_DATE && suggestion.taskId && suggestion.toDate && task) {
      const start = new Date(task.startDate || task.start || task.dueDate || Date.now());
      const end = new Date(task.dueDate || task.end || task.startDate || start);
      const durationDays = Math.max(0, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
      const newStart = new Date(suggestion.toDate);
      const newEnd = new Date(newStart);
      newEnd.setDate(newEnd.getDate() + durationDays);
      await updateTask?.(suggestion.taskId, {
        start: newStart.toISOString(),
        startDate: newStart.toISOString(),
        end: newEnd.toISOString(),
        dueDate: newEnd.toISOString(),
      });
    }

    const message = suggestion?.message || suggestion?.title || 'Suggestion applied';
    notifyAndInbox?.({
      notification: {
        id: `planner-suggestion-${suggestion?.id || Date.now()}`,
        title: "Suggestion applied",
        message,
        level: "success",
        source: "planner",
        world: "studio",
        entity: suggestion?.taskId ? { type: "task", id: suggestion.taskId } : null,
        action: suggestion?.taskId
          ? { kind: "NAVIGATE", to: "/dashboard/planner/tasks", params: { eventId: suggestion.taskId } }
          : { kind: "NAVIGATE", to: "/dashboard/planner/tasks" },
      },
    });
  };

  // Emit alerts to global notifications (UI-only, no data changes)
  useEffect(() => {
    if (!addNotification) return;
    alerts.forEach((alert) => {
      const key = `planner-${alert.id}`;
      if (addedAlertIds.current.has(key)) return;
      addNotification({
        id: key,
        title: alert.title,
        body: alert.message,
        severity: alert.severity,
        type: 'task',
        world: 'studio',
        createdAt: alert.createdAt || new Date().toISOString(),
        readAt: alert.read ? new Date().toISOString() : null,
        entityType: alert.entityType || 'planner_event',
        entityId: alert.entityId,
        route: alert.entityId
          ? `/dashboard/planner/tasks?eventId=${alert.entityId}`
          : "/dashboard/planner/tasks",
      });
      addedAlertIds.current.add(key);
    });
  }, [alerts, addNotification]);
  return (
    <PlannerProvider>
      <PageLayout
        header={<PageHeader title="Planner" showBack actions={(
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="primary" onClick={() => setShowGenerationModal(true)} slide>
              Generate Production Plan
            </Button>
          </div>
        )} />}
        toolbar={
          <div className="bg-[var(--mudo-bg-soft)]/80 backdrop-blur border-b border-[var(--mudo-border)]">
            <div className="mx-auto max-w-[1100px] px-4 sm:px-8 py-2">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <div className="flex items-center gap-1 sm:gap-2">
                  <Button variant={view === 'list' ? 'primary' : 'soft'} onClick={() => setView('list')}>List</Button>
                  <Button variant={view === 'kanban' ? 'primary' : 'soft'} onClick={() => setView('kanban')}>Kanban</Button>
                  <Button variant={view === 'calendar' ? 'primary' : 'soft'} onClick={() => setView('calendar')}>Calendar</Button>
                  <Button variant={view === 'gantt' ? 'primary' : 'soft'} onClick={() => setView('gantt')}>Gantt</Button>
                </div>
                <div className="flex flex-wrap items-center gap-2 ml-auto">
                  {/* Filters: status, date range, product — UI-only, logic unchanged */}
                  <Button variant="soft">Status</Button>
                  <Button variant="soft">Date range</Button>
                  <Button variant="soft">Product</Button>
                </div>
              </div>
            </div>
          </div>
        }
      >
        {computedCapacity && (
          <div className="mt-2 mb-3 text-sm text-[var(--mudo-text)]">
            Studio load: {capacityLevelLabel(computedCapacity)}
          </div>
        )}

        {alerts?.some((a) => a.severity === 'error') && (
          <WidgetShell title="Attention" className="mb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SmallText className="text-[var(--mudo-text)]">
                You have {alerts.filter((a) => a.severity === 'error').length} overdue tasks.
              </SmallText>
              <Button variant="soft" onClick={() => setView('list')}>
                View
              </Button>
            </div>
          </WidgetShell>
        )}

        {suggestions && suggestions.length > 0 && (
          <WidgetShell title="Suggested actions" className="mb-3">
            <div className="space-y-2">
              {suggestions.slice(0, 3).map((s) => (
                <div key={s.id} className="flex items-start justify-between gap-3">
                  <div className="text-sm">
                    <div className="font-medium text-[var(--mudo-text)]">{s.title}</div>
                    <SmallText className="opacity-70">{s.message}</SmallText>
                  </div>
                  <Button variant="soft" onClick={() => applySuggestion(s)}>
                    Apply
                  </Button>
                </div>
              ))}
              {suggestions.length > 3 && (
                <SmallText className="opacity-70">{suggestions.length - 3} more suggestions available</SmallText>
              )}
            </div>
          </WidgetShell>
        )}
        {alerts && alerts.length > 0 && (
          <WidgetShell title="Alerts" className="mb-3">
            <div className="space-y-2">
              {alerts.map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-3">
                  <div className="text-sm">
                    <div className="font-medium text-[var(--mudo-text)]">{a.title}</div>
                    <SmallText className="opacity-70">{a.message}</SmallText>
                  </div>
                  <Button variant="soft">Review</Button>
                </div>
              ))}
            </div>
          </WidgetShell>
        )}
        <GeneratePlanSheet
          open={showGenerationModal}
          onClose={() => setShowGenerationModal(false)}
          products={products}
          onSubmit={(tasks) => {
            addGeneratedTasks?.(tasks);
          }}
        />
        {view === 'list' && (
          <>
            {children}
            <ListView />
          </>
        )}
        {view === 'kanban' && <KanbanView />}
        {view === 'calendar' && <CalendarView />}
        {view === 'gantt' && <GanttView />}
      </PageLayout>
    </PlannerProvider>
  );
}

export default function PlannerShell(props) {
  return (
    <PlannerProvider>
      <PlannerShellContent {...props} />
    </PlannerProvider>
  );
}
